'use client'

import { useState, useCallback } from 'react'
import { toast } from 'sonner'

interface OperationState {
  loading: boolean
  error: string | null
}

interface UseOperationsResult<T> {
  state: OperationState
  execute: (operation: () => Promise<T>) => Promise<void>
  reset: () => void
}

export function useOperations<T = any>(): UseOperationsResult<T> {
  const [state, setState] = useState<OperationState>({
    loading: false,
    error: null
  })

  const execute = useCallback(async (operation: () => Promise<T>) => {
    setState({ loading: true, error: null })
    
    try {
      await operation()
      setState({ loading: false, error: null })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      setState({ loading: false, error: errorMessage })
      toast.error(errorMessage)
    }
  }, [])

  const reset = useCallback(() => {
    setState({ loading: false, error: null })
  }, [])

  return {
    state,
    execute,
    reset
  }
}

interface UseMultiOperationsResult<T> {
  states: Record<string, OperationState>
  execute: (operationName: string, operation: () => Promise<T>) => Promise<void>
  reset: (operationName?: string) => void
}

export function useMultiOperations<T = any>(): UseMultiOperationsResult<T> {
  const [states, setStates] = useState<Record<string, OperationState>>({})

  const execute = useCallback(async (operationName: string, operation: () => Promise<T>) => {
    setStates(prev => ({
      ...prev,
      [operationName]: { loading: true, error: null }
    }))
    
    try {
      await operation()
      setStates(prev => ({
        ...prev,
        [operationName]: { loading: false, error: null }
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      setStates(prev => ({
        ...prev,
        [operationName]: { loading: false, error: errorMessage }
      }))
      toast.error(errorMessage)
    }
  }, [])

  const reset = useCallback((operationName?: string) => {
    if (operationName) {
      setStates(prev => {
        const newStates = { ...prev }
        delete newStates[operationName]
        return newStates
      })
    } else {
      setStates({})
    }
  }, [])

  return {
    states,
    execute,
    reset
  }
}